/**
 * Система аналитики и логирования
 * Логирует все действия пользователя и API запросы
 * В dev - логи в консоль
 */

export type AnalyticsEventType =
  | "api_request_start"
  | "api_request_end"
  | "api_request_error"
  | "button_click"
  | "ui_action"
  | "navigation"
  | "screen_view"
  | "form_submit"
  | "form_change"
  | "modal_open"
  | "modal_close";

export interface AnalyticsEvent {
  type: AnalyticsEventType;
  name: string;
  timestamp: number;
  duration?: number; // в миллисекундах
  metadata?: Record<string, any>;
  url?: string;
  method?: string;
  statusCode?: number;
  error?: string;
}

class Analytics {
  private events: AnalyticsEvent[] = [];
  private requestTimers: Map<string, number> = new Map();
  // В dev режиме (npm run dev) PROD = false, в production build (npm run build) PROD = true
  private isProduction = import.meta.env.PROD;

  /**
   * Форматирует время для логов
   */
  private formatTime(timestamp: number): string {
    return new Date(timestamp).toISOString();
  }

  /**
   * Логирует событие в консоль
   */
  private log(event: AnalyticsEvent): void {
    // Сохраняем событие для будущей отправки на бэк (если нужно)
    this.events.push(event);

    // Ограничиваем размер массива (последние 1000 событий)
    if (this.events.length > 1000) {
      this.events = this.events.slice(-1000);
    }
  }

  /**
   * Логирует начало API запроса
   */
  logApiRequestStart(url: string, method: string, metadata?: Record<string, any>): string {
    const requestId = `${method}_${url}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const timestamp = Date.now();
    
    this.requestTimers.set(requestId, timestamp);

    this.log({
      type: "api_request_start",
      name: `${method} ${url}`,
      timestamp,
      url,
      method,
      metadata: {
        ...metadata,
        requestId
      }
    });

    return requestId;
  }

  /**
   * Логирует завершение API запроса
   */
  logApiRequestEnd(
    requestId: string,
    url: string,
    method: string,
    statusCode: number,
    metadata?: Record<string, any>
  ): void {
    const startTime = this.requestTimers.get(requestId);
    const endTime = Date.now();
    const duration = startTime ? endTime - startTime : undefined;

    if (startTime) {
      this.requestTimers.delete(requestId);
    }

    this.log({
      type: "api_request_end",
      name: `${method} ${url}`,
      timestamp: endTime,
      duration,
      url,
      method,
      statusCode,
      metadata: {
        ...metadata,
        requestId
      }
    });
  }

  /**
   * Логирует ошибку API запроса
   */
  logApiRequestError(
    requestId: string,
    url: string,
    method: string,
    error: string,
    metadata?: Record<string, any>
  ): void {
    const startTime = this.requestTimers.get(requestId);
    const endTime = Date.now();
    const duration = startTime ? endTime - startTime : undefined;

    if (startTime) {
      this.requestTimers.delete(requestId);
    }

    this.log({
      type: "api_request_error",
      name: `${method} ${url}`,
      timestamp: endTime,
      duration,
      url,
      method,
      error,
      metadata: {
        ...metadata,
        requestId
      }
    });
  }

  /**
   * Логирует клик по кнопке
   */
  logButtonClick(buttonName: string, metadata?: Record<string, any>): void {
    this.log({
      type: "button_click",
      name: buttonName,
      timestamp: Date.now(),
      metadata: {
        ...metadata,
        page: window.location.pathname
      }
    });
  }

  /**
   * Логирует UI действие
   */
  logUIAction(actionName: string, metadata?: Record<string, any>): void {
    this.log({
      type: "ui_action",
      name: actionName,
      timestamp: Date.now(),
      metadata: {
        ...metadata,
        page: window.location.pathname
      }
    });
  }

  /**
   * Логирует навигацию
   */
  logNavigation(from: string, to: string, metadata?: Record<string, any>): void {
    this.log({
      type: "navigation",
      name: `${from} -> ${to}`,
      timestamp: Date.now(),
      metadata: {
        from,
        to,
        ...metadata
      }
    });
  }

  /**
   * Логирует просмотр экрана
   */
  logScreenView(screenName: string, metadata?: Record<string, any>): void {
    this.log({
      type: "screen_view",
      name: screenName,
      timestamp: Date.now(),
      metadata: {
        ...metadata,
        url: window.location.pathname
      }
    });
  }

  /**
   * Логирует отправку формы
   */
  logFormSubmit(formName: string, metadata?: Record<string, any>): void {
    this.log({
      type: "form_submit",
      name: formName,
      timestamp: Date.now(),
      metadata: {
        ...metadata,
        page: window.location.pathname
      }
    });
  }

  /**
   * Логирует изменение формы
   */
  logFormChange(formName: string, fieldName: string, metadata?: Record<string, any>): void {
    this.log({
      type: "form_change",
      name: `${formName}.${fieldName}`,
      timestamp: Date.now(),
      metadata: {
        formName,
        fieldName,
        ...metadata,
        page: window.location.pathname
      }
    });
  }

  /**
   * Логирует открытие модального окна
   */
  logModalOpen(modalName: string, metadata?: Record<string, any>): void {
    this.log({
      type: "modal_open",
      name: modalName,
      timestamp: Date.now(),
      metadata: {
        ...metadata,
        page: window.location.pathname
      }
    });
  }

  /**
   * Логирует закрытие модального окна
   */
  logModalClose(modalName: string, duration?: number, metadata?: Record<string, any>): void {
    this.log({
      type: "modal_close",
      name: modalName,
      timestamp: Date.now(),
      duration,
      metadata: {
        ...metadata,
        page: window.location.pathname
      }
    });
  }

  /**
   * Идентифицировать пользователя
   */
  identify(userId: string, properties?: Record<string, any>) {
    // Метод для идентификации пользователя (можно расширить для отправки на бэк)
  }

  /**
   * Установить свойства пользователя
   */
  setUserProperties(properties: Record<string, any>) {
    // Метод для установки свойств пользователя (можно расширить для отправки на бэк)
  }

  /**
   * Получить все события (для отправки на бэк)
   */
  getEvents(): AnalyticsEvent[] {
    return [...this.events];
  }

  /**
   * Очистить события
   */
  clearEvents(): void {
    this.events = [];
  }
}

export const analytics = new Analytics();

